import React, { useEffect, useState, useRef } from "react";
import {
  Box, Grid, Paper, Stack, Typography, Button, Chip,
  Divider, Tooltip, IconButton, Avatar, Select, MenuItem,
  TextField, TextareaAutosize, Modal, Radio, RadioGroup,
  FormControlLabel, Badge,
} from "@mui/material";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/store";
import { logout } from "redux/features/userSlice";
import { getCookie, removeCookie } from "../../../utils/storageUtils";
import { usePlanDetailsMutation } from "../../../redux/api/productApi";
import { toast } from "react-toastify";

// ── Icons ──
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import BoltIcon from "@mui/icons-material/Bolt";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SendIcon from "@mui/icons-material/Send";
import RepeatIcon from "@mui/icons-material/Repeat";
import QueueIcon from "@mui/icons-material/Queue";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";

// ── Sidebar assets ──
import ocesha_logo from "../../../assets/ocesha_images/ocesha_logo.png";
import { ReactComponent as BlogsIco }     from "../../../assets/ocesha_images/blogs_ico.svg";
import { ReactComponent as ImagesIco }    from "../../../assets/ocesha_images/images_ico.svg";
import { ReactComponent as ClipIco }      from "../../../assets/ocesha_images/clip_ico.svg";
import { ReactComponent as UpgradeIco }   from "../../../assets/ocesha_images/upgrade_ico.svg";
import { ReactComponent as BillingIco }   from "../../../assets/ocesha_images/billing_ico.svg";
import { ReactComponent as SettingsIco }  from "../../../assets/ocesha_images/settings_ico.svg";
import { ReactComponent as LogoutIco }    from "../../../assets/ocesha_images/logout_ico.svg";
import { ReactComponent as DashboardIco } from "../../../assets/ocesha_images/dashboard_ico.svg";
import youtubeIco1 from "../../../assets/traffic_monster/youtubeIco_1.png";

// ── Theme ──
const cardBg      = "#15151D";
const border      = "#1f2230";
const accent      = "#4dabf7";
const mutedText   = "#9aa0b4";
const accentGreen = "#32d583";

// ── Month names ──
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// ── Day headers ──
const CAL_DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

// ─────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────
type PostStatus = "Published" | "Draft" | "Need Approval" | "Scheduled";
type ViewMode   = "list" | "new_post";

interface MarketingPost {
  id: string;
  checked: boolean;
  content: string;
  status: PostStatus;
  date: string;
  tags: string[];
  postType: string;
  avatars: string[];
}

// ── Status badge colours ──
const STATUS_COLORS: Record<PostStatus, { bg: string; color: string }> = {
  Published:       { bg: "rgba(50,213,131,0.15)",  color: "#32d583" },
  Draft:           { bg: "rgba(156,163,175,0.15)", color: "#9ca3af" },
  "Need Approval": { bg: "rgba(245,158,11,0.15)",  color: "#f59e0b" },
  Scheduled:       { bg: "rgba(77,171,247,0.15)",  color: accent    },
};

// ── Tag colours ──
const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  "Tips & Tricks": { bg: "rgba(139,92,246,0.2)",  color: "#a78bfa" },
  Post:            { bg: "rgba(77,171,247,0.2)",   color: accent    },
  Event:           { bg: "rgba(52,211,153,0.2)",   color: "#34d399" },
  Announcement:    { bg: "rgba(251,146,60,0.2)",   color: "#fb923c" },
  LinkedIn:        { bg: "rgba(10,102,194,0.2)",   color: "#0a66c2" },
  Twitter:         { bg: "rgba(29,161,242,0.2)",   color: "#1da1f2" },
};

// ── Mock posts ──
const MOCK_POSTS: MarketingPost[] = [
  { id:"1", checked:false, content:"LinkedIn Thought Leadership Post - Chapter 1: Artificial Intelligence. But AI in convergence is transformative.", status:"Published", date:"Dec 25, 2025 10:20 AM", tags:["Tips & Tricks","Post"], postType:"linkedin", avatars:[] },
  { id:"2", checked:false, content:"The failure rate is brutal. 90%+ of startups don't make it....", status:"Published", date:"Dec 25, 2025 10:20 AM", tags:["Tips & Tricks","Post"], postType:"linkedin", avatars:[] },
  { id:"3", checked:false, content:"The failure rate is brutal. 90%+ of startups don't make it....", status:"Draft", date:"Dec 25, 2025 10:20 AM", tags:["Event","Tips & Tricks","Post"], postType:"twitter", avatars:[] },
  { id:"4", checked:false, content:"In The Convergence of AI and the Top 10 Emerging Technologies, I start with AI for on...", status:"Need Approval", date:"Dec 25, 2025 10:20 AM", tags:["Announcement"], postType:"linkedin", avatars:[] },
  { id:"5", checked:false, content:"We explore emerging technologies based on our time researching at Capital Group Americ...", status:"Need Approval", date:"Dec 25, 2025 10:20 AM", tags:["Post"], postType:"linkedin", avatars:[] },
];

// ── Social accounts mock ──
const SOCIAL_ACCOUNTS = Array(8).fill({ name: "Ocesha Official", img: "/images/blogs5.png" });

// ─────────────────────────────────────────────────────
// Calendar helpers
// ─────────────────────────────────────────────────────
function buildCalendarGrid(year: number, month: number): (Date | null)[][] {
  const firstDow    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const cells: (Date | null)[] = [];

  for (let i = 0; i < firstDow; i++) {
    cells.push(new Date(year, month - 1, daysInPrev - firstDow + 1 + i));
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(year, month, d));
  }
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let d = 1; d <= 7 - remainder; d++) {
      cells.push(new Date(year, month + 1, d));
    }
  }
  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}
function isSameMonth(date: Date, year: number, month: number) {
  return date.getFullYear() === year && date.getMonth() === month;
}
function isBetween(date: Date, from: Date, to: Date) {
  const d = date.getTime();
  return d > from.getTime() && d < to.getTime();
}
function formatDisplayDate(d: Date) {
  return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
}

// ─────────────────────────────────────────────────────
// DateRangePicker — inline dropdown with two month calendars
// ─────────────────────────────────────────────────────
interface DateRangePickerProps {
  open: boolean;
  onClose: () => void;
  fromDate: Date | null;
  toDate: Date | null;
  onApply: (from: Date, to: Date) => void;
  anchorRef: React.RefObject<HTMLElement>;
}

function DateRangePicker({ open, onClose, fromDate, toDate, onApply, anchorRef }: DateRangePickerProps) {
  const today = new Date();
  const [leftView,  setLeftView]  = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [rightView, setRightView] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 1));
  const [selecting, setSelecting] = useState<"from"|"to">("from");
  const [tempFrom,  setTempFrom]  = useState<Date | null>(fromDate);
  const [tempTo,    setTempTo]    = useState<Date | null>(toDate);
  const [hovered,   setHovered]   = useState<Date | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const leftYear   = leftView.getFullYear();
  const leftMonth  = leftView.getMonth();
  const rightYear  = rightView.getFullYear();
  const rightMonth = rightView.getMonth();

  const prevMonth = () => {
    const d = new Date(leftYear, leftMonth - 1, 1);
    setLeftView(d);
    setRightView(new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };
  const nextMonth = () => {
    const d = new Date(rightYear, rightMonth + 1, 1);
    setRightView(d);
    setLeftView(new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };

  const handleDayClick = (date: Date) => {
    if (selecting === "from") {
      setTempFrom(date);
      setTempTo(null);
      setSelecting("to");
    } else {
      if (tempFrom && date < tempFrom) {
        setTempFrom(date);
        setTempTo(null);
        setSelecting("to");
      } else {
        setTempTo(date);
        setSelecting("from");
      }
    }
  };

  const handleApply = () => {
    if (tempFrom && tempTo) {
      onApply(tempFrom, tempTo);
      onClose();
    }
  };

  const renderMonth = (year: number, month: number) => {
    const weeks = buildCalendarGrid(year, month);
    return (
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography fontSize={13} fontWeight={700} color="#fff" textAlign="center" mb={1.5}>
          {MONTH_NAMES[month]} {year}
        </Typography>
        <Grid container columns={7} mb={0.5}>
          {CAL_DAYS.map(d => (
            <Grid item xs={1} key={d} sx={{ textAlign:"center" }}>
              <Typography fontSize={11} color={mutedText} fontWeight={600}>{d}</Typography>
            </Grid>
          ))}
        </Grid>
        {weeks.map((week, wi) => (
          <Grid container columns={7} key={wi} mb={0.2}>
            {week.map((date, di) => {
              if (!date) return <Grid item xs={1} key={di}/>;
              const isCurrentMonth = isSameMonth(date, year, month);
              const isFrom         = tempFrom ? isSameDay(date, tempFrom) : false;
              const isTo           = tempTo   ? isSameDay(date, tempTo)   : false;
              const isToday        = isSameDay(date, today);
              const effectiveTo    = selecting === "to" && hovered ? hovered : tempTo;
              const inRange        = isCurrentMonth && tempFrom && effectiveTo
                ? isBetween(date, tempFrom, effectiveTo) : false;
              const isEndpoint     = (isFrom || isTo) && isCurrentMonth;

              return (
                <Grid item xs={1} key={di} sx={{ textAlign:"center", position:"relative" }}>
                  {/* Range band */}
                  {inRange && (
                    <Box sx={{ position:"absolute", top:"50%", left:0, right:0, height:28, transform:"translateY(-50%)", background:"rgba(77,171,247,0.12)", zIndex:0 }}/>
                  )}
                  {/* Half-band left for "to" */}
                  {isTo && isCurrentMonth && tempFrom && (
                    <Box sx={{ position:"absolute", top:"50%", left:0, right:"50%", height:28, transform:"translateY(-50%)", background:"rgba(77,171,247,0.12)", zIndex:0 }}/>
                  )}
                  {/* Half-band right for "from" */}
                  {isFrom && isCurrentMonth && (tempTo || (selecting==="to" && hovered)) && (
                    <Box sx={{ position:"absolute", top:"50%", left:"50%", right:0, height:28, transform:"translateY(-50%)", background:"rgba(77,171,247,0.12)", zIndex:0 }}/>
                  )}
                  <Box
                    onClick={() => isCurrentMonth && handleDayClick(date)}
                    onMouseEnter={() => setHovered(date)}
                    onMouseLeave={() => setHovered(null)}
                    sx={{
                      position:"relative", zIndex:1,
                      width:28, height:28, borderRadius:"50%", mx:"auto",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      cursor: isCurrentMonth ? "pointer" : "default",
                      background: isEndpoint ? accent : "transparent",
                      border: isToday && !isEndpoint ? `1.5px solid ${accent}` : "none",
                      opacity: !isCurrentMonth ? 0.25 : 1,
                      "&:hover": isCurrentMonth && !isEndpoint ? { background:"#2a2c3e" } : {},
                      transition:"background 0.12s",
                    }}
                  >
                    <Typography fontSize={12} fontWeight={isEndpoint ? 700 : 400}
                      color={isEndpoint ? "#000" : isToday ? accent : "#c8d0e0"}>
                      {date.getDate()}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
    );
  };

  return (
    <Box
      ref={popupRef}
      sx={{
        position:"absolute", top:"calc(100% + 8px)", left:0, zIndex:9999,
        background:"#1a1a26", border:`1px solid ${border}`,
        borderRadius:"16px", p:"20px 24px",
        boxShadow:"0 16px 48px rgba(0,0,0,0.5)",
        width: isMobile ? 320 : 620,
      }}
    >
      {/* Header hint */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography fontSize={13} fontWeight={600} color={mutedText}>
          {selecting === "from" ? "📅 Select start date" : "📅 Select end date"}
        </Typography>
        {tempFrom && (
          <Typography fontSize={12} color={accent} fontWeight={600}>
            {formatDisplayDate(tempFrom)}
            {tempTo
              ? ` → ${formatDisplayDate(tempTo)}`
              : selecting==="to" && hovered ? ` → ${formatDisplayDate(hovered)}` : ""}
          </Typography>
        )}
      </Stack>

      {/* Two calendars */}
      <Stack direction={isMobile ? "column" : "row"} spacing={0} alignItems="flex-start">
        {/* Left month */}
        <Box sx={{ flex:1 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={0.5}>
            <IconButton size="small" onClick={prevMonth} sx={{ color:mutedText, p:0.4 }}>
              <NavigateBeforeIcon sx={{ fontSize:18 }}/>
            </IconButton>
          </Stack>
          {renderMonth(leftYear, leftMonth)}
        </Box>

        {/* Divider */}
        {!isMobile && (
          <Box sx={{ width:1, background:border, mx:2, alignSelf:"stretch" }}/>
        )}

        {/* Right month */}
        <Box sx={{ flex:1 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={0.5}>
            <IconButton size="small" onClick={nextMonth} sx={{ color:mutedText, p:0.4 }}>
              <NavigateNextIcon sx={{ fontSize:18 }}/>
            </IconButton>
          </Stack>
          {renderMonth(rightYear, rightMonth)}
        </Box>
      </Stack>

      {/* Quick presets */}
      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={0.5}>
        {[
          { label:"Today",       fn:() => { const d=new Date(); setTempFrom(d); setTempTo(d); setSelecting("from"); } },
          { label:"Last 7 days", fn:() => { const t=new Date(); const f=new Date(); f.setDate(f.getDate()-6); setTempFrom(f); setTempTo(t); setSelecting("from"); } },
          { label:"This month",  fn:() => { const t=new Date(); const f=new Date(t.getFullYear(),t.getMonth(),1); setTempFrom(f); setTempTo(t); setSelecting("from"); } },
          { label:"Last month",  fn:() => { const t=new Date(); const f=new Date(t.getFullYear(),t.getMonth()-1,1); const e=new Date(t.getFullYear(),t.getMonth(),0); setTempFrom(f); setTempTo(e); setSelecting("from"); } },
        ].map(p => (
          <Button key={p.label} size="small" onClick={p.fn}
            sx={{ textTransform:"none", fontSize:11, fontWeight:600, color:mutedText, background:"#1e2030", border:`1px solid ${border}`, borderRadius:"6px", px:1.5, py:0.4, "&:hover":{ color:"#fff", borderColor:accent } }}>
            {p.label}
          </Button>
        ))}
      </Stack>

      {/* Actions */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
        <Button onClick={onClose} size="small"
          sx={{ textTransform:"none", fontSize:12, color:mutedText, border:`1px solid ${border}`, borderRadius:"8px", px:2 }}>
          Cancel
        </Button>
        <Button onClick={handleApply} disabled={!tempFrom || !tempTo} size="small"
          sx={{
            textTransform:"none", fontSize:12, fontWeight:700,
            background: tempFrom && tempTo ? accent : "#2a2c3e",
            color: tempFrom && tempTo ? "#000" : mutedText,
            borderRadius:"8px", px:2,
            "&:hover":{ background: tempFrom && tempTo ? "#74c0fc" : "#2a2c3e" },
          }}>
          Apply
        </Button>
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────
// StatusBadge
// ─────────────────────────────────────────────────────
function StatusBadge({ status }: { status: PostStatus }) {
  const s = STATUS_COLORS[status];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center"
      sx={{ background:s.bg, borderRadius:"6px", px:1.2, py:0.35, display:"inline-flex", whiteSpace:"nowrap" }}>
      {status === "Published" && <CheckCircleIcon sx={{ fontSize:11, color:s.color }}/>}
      {status === "Scheduled" && <ScheduleIcon    sx={{ fontSize:11, color:s.color }}/>}
      <Typography fontSize={11} fontWeight={700} color={s.color}>{status}</Typography>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────
// TagChip
// ─────────────────────────────────────────────────────
function TagChip({ label }: { label: string }) {
  const s = TAG_COLORS[label] || { bg:"rgba(100,100,120,0.2)", color:"#9aa0b4" };
  return (
    <Box sx={{ background:s.bg, borderRadius:"5px", px:1, py:0.3, display:"inline-flex" }}>
      <Typography fontSize={10} fontWeight={700} color={s.color}>{label}</Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────
// PostTypeIcon small
// ─────────────────────────────────────────────────────
function SmallTypeIcon({ type }: { type: string }) {
  if (type === "linkedin") return <LinkedInIcon sx={{ fontSize:16, color:"#0a66c2" }}/>;
  if (type === "twitter")  return <TwitterIcon  sx={{ fontSize:16, color:"#1da1f2" }}/>;
  return <ShareOutlinedIcon sx={{ fontSize:16, color:mutedText }}/>;
}

// ─────────────────────────────────────────────────────
// Connect Social Modal
// ─────────────────────────────────────────────────────
function ConnectSocialModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position:"absolute", top:"50%", left:"50%",
        transform:"translate(-50%,-50%)",
        width: isMobile ? "90%" : 580,
        background:"#1a1a26", borderRadius:"16px",
        border:`1px solid ${border}`, p:3,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography fontSize={16} fontWeight={700} color="#fff">Connect Social</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<AddIcon/>}
              sx={{ textTransform:"none", fontSize:12, fontWeight:600, background:accent, color:"#000", borderRadius:"8px", px:1.5, py:0.5, "&:hover":{ background:"#74c0fc" } }}>
              Add Socials
            </Button>
            <IconButton size="small" onClick={onClose} sx={{ color:mutedText }}>
              <CloseIcon sx={{ fontSize:18 }}/>
            </IconButton>
          </Stack>
        </Stack>
        <Grid container spacing={1.5}>
          {SOCIAL_ACCOUNTS.map((acc, i) => (
            <Grid item xs={3} key={i}>
              <Paper elevation={0} sx={{ p:"12px 10px", borderRadius:"10px", background:cardBg, border:`1px solid ${border}`, textAlign:"center", cursor:"pointer", "&:hover":{ borderColor:accent }, position:"relative" }}>
                <IconButton size="small" sx={{ position:"absolute", right:4, top:4, color:mutedText, p:0.2 }}>
                  <MoreHorizIcon sx={{ fontSize:14 }}/>
                </IconButton>
                <Avatar src={acc.img} sx={{ width:44, height:44, mx:"auto", mb:0.8 }}/>
                <Typography fontSize={11} fontWeight={600} color="#c8d0e0" noWrap>{acc.name}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────
// Schedule Modal
// ─────────────────────────────────────────────────────
function ScheduleModal({
  open, onClose, title = "Schedule Post",
}: { open: boolean; onClose: () => void; title?: string; }) {
  const today = new Date();
  const [viewDate,     setViewDate]     = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [hour,   setHour]   = useState("05");
  const [minute, setMinute] = useState("00");
  const [ampm,   setAmpm]   = useState("PM");

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const weeks = buildCalendarGrid(year, month);

  const handleDayClick = (date: Date) => {
    const d = new Date(date); d.setHours(0,0,0,0);
    const t = new Date(today); t.setHours(0,0,0,0);
    if (d < t) return;
    setSelectedDate(date);
  };

  const handleBestTime = (timeRange: string) => {
    const startPart = timeRange.split(" - ")[0].trim();
    const parts = startPart.split(" ");
    const [h, m] = parts[0].split(":");
    setHour(String(parseInt(h)).padStart(2,"0"));
    setMinute(m || "00");
    setAmpm(parts[1] || "AM");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position:"absolute", top:"50%", left:"50%",
        transform:"translate(-50%,-50%)",
        width: isMobile ? "90%" : 420,
        background:"#1a1a26", borderRadius:"16px",
        border:`1px solid ${border}`, p:3,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography fontSize={15} fontWeight={700} color="#fff">{title}</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color:mutedText }}>
            <CloseIcon sx={{ fontSize:18 }}/>
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <IconButton size="small" onClick={() => setViewDate(new Date(year,month-1,1))} sx={{ color:mutedText }}>
            <NavigateBeforeIcon sx={{ fontSize:18 }}/>
          </IconButton>
          <Typography fontSize={14} fontWeight={700} color="#fff">{MONTH_NAMES[month]} {year}</Typography>
          <IconButton size="small" onClick={() => setViewDate(new Date(year,month+1,1))} sx={{ color:mutedText }}>
            <NavigateNextIcon sx={{ fontSize:18 }}/>
          </IconButton>
        </Stack>

        <Grid container columns={7} mb={0.5}>
          {CAL_DAYS.map(d => (
            <Grid item xs={1} key={d} sx={{ textAlign:"center" }}>
              <Typography fontSize={11} color={mutedText} fontWeight={600}>{d}</Typography>
            </Grid>
          ))}
        </Grid>

        {weeks.map((week, wi) => (
          <Grid container columns={7} key={wi} mb={0.3}>
            {week.map((date, di) => {
              if (!date) return <Grid item xs={1} key={di}/>;
              const isCurrentMonth = isSameMonth(date, year, month);
              const isToday        = isSameDay(date, today);
              const isSelected     = selectedDate ? isSameDay(date, selectedDate) : false;
              const pastDay = (() => { const d=new Date(date); d.setHours(0,0,0,0); const t=new Date(today); t.setHours(0,0,0,0); return d<t; })();
              return (
                <Grid item xs={1} key={di} sx={{ textAlign:"center" }}>
                  <Box onClick={() => !pastDay && isCurrentMonth && handleDayClick(date)}
                    sx={{
                      width:30, height:30, borderRadius:"50%", mx:"auto",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      cursor: pastDay || !isCurrentMonth ? "default" : "pointer",
                      background: isSelected ? accent : "transparent",
                      border: isToday && !isSelected ? `1.5px solid ${accent}` : "none",
                      opacity: !isCurrentMonth || pastDay ? 0.3 : 1,
                      "&:hover": (!pastDay && isCurrentMonth && !isSelected) ? { background:"#2a2c3e" } : {},
                      transition:"background 0.15s",
                    }}>
                    <Typography fontSize={12} fontWeight={isSelected?700:400}
                      color={isSelected?"#000":isToday?accent:"#c8d0e0"}>
                      {date.getDate()}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ))}

        <Stack direction="row" alignItems="center" spacing={1.5} mt={2} mb={1}>
          <Typography fontSize={13} fontWeight={600} color={mutedText}>Time to Post:</Typography>
          {[
            { value:hour,   onChange:(v:string)=>setHour(v),   items:Array.from({length:12},(_,i)=>String(i+1).padStart(2,"0")), width:52 },
            { value:minute, onChange:(v:string)=>setMinute(v), items:["00","05","10","15","20","25","30","35","40","45","50","55"], width:52 },
            { value:ampm,   onChange:(v:string)=>setAmpm(v),   items:["AM","PM"], width:60 },
          ].map((sel,i) => (
            <Select key={i} value={sel.value} onChange={e=>sel.onChange(e.target.value)} size="small" variant="outlined"
              sx={{ color:"#fff", fontSize:12, fontWeight:700, background:"#0f1016", borderRadius:"6px",
                "& .MuiOutlinedInput-notchedOutline":{ borderColor:border },
                "& .MuiSelect-icon":{ color:mutedText }, minWidth:sel.width }}>
              {sel.items.map(v => <MenuItem key={v} value={v} sx={{ fontSize:12 }}>{v}</MenuItem>)}
            </Select>
          ))}
        </Stack>

        {selectedDate && (
          <Box sx={{ background:"rgba(77,171,247,0.06)", border:`1px solid ${accent}22`, borderRadius:"8px", px:2, py:0.8, mb:1.5 }}>
            <Typography fontSize={12} color={accent} fontWeight={600}>
              📅 Scheduled for: {selectedDate.toLocaleDateString("en-US",{weekday:"short",month:"long",day:"numeric",year:"numeric"})} at {hour}:{minute} {ampm}
            </Typography>
          </Box>
        )}

        <Box sx={{ background:"rgba(77,171,247,0.08)", border:`1px solid ${accent}33`, borderRadius:"8px", p:"10px 14px", mb:2 }}>
          <Stack direction="row" spacing={0.6} alignItems="center" mb={0.5}>
            <CheckCircleIcon sx={{ fontSize:13, color:accentGreen }}/>
            <Typography fontSize={12} fontWeight={700} color={accentGreen}>Best Time to Post</Typography>
            <InfoOutlinedIcon sx={{ fontSize:12, color:mutedText }}/>
          </Stack>
          <Typography fontSize={11} color={mutedText} mb={1}>(GMT-0800 America/Los_Angeles)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            {["7:00 PM - 8:00 PM","5:00 PM - 6:00 PM","9:00 AM - 10:00 AM"].map(t => (
              <Box key={t} onClick={() => handleBestTime(t)}
                sx={{ background:"#1e2030", border:`1px solid ${border}`, borderRadius:"6px", px:1, py:0.4, cursor:"pointer", "&:hover":{ borderColor:accent } }}>
                <Typography fontSize={10} fontWeight={600} color="#c8d0e0">{t}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose} variant="outlined"
            sx={{ textTransform:"none", fontSize:12, color:mutedText, borderColor:border, borderRadius:"8px" }}>
            Cancel
          </Button>
          <Button startIcon={<ScheduleIcon/>} disabled={!selectedDate}
            sx={{
              textTransform:"none", fontSize:12, fontWeight:700,
              background: selectedDate ? accent : "#2a2c3e",
              color: selectedDate ? "#000" : mutedText,
              borderRadius:"8px", px:2,
              "&:hover":{ background: selectedDate ? "#74c0fc" : "#2a2c3e" },
            }}>
            Schedule Post
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────
// Post Options Dropdown
// ─────────────────────────────────────────────────────
function PostOptionsDropdown({ open, onClose, onSelect }: { open:boolean; onClose:()=>void; onSelect:(v:string)=>void }) {
  const options = [
    { icon:<ScheduleIcon sx={{fontSize:16}}/>, label:"Schedule Post",            desc:"Schedule a post for a later date or time." },
    { icon:<HowToRegIcon sx={{fontSize:16}}/>, label:"Send Post for Approval",   desc:"Send for approval first within your agency or organization." },
    { icon:<RepeatIcon   sx={{fontSize:16}}/>, label:"Schedule Recurring Posts", desc:"Send for approval first within your agency or organization." },
    { icon:<QueueIcon    sx={{fontSize:16}}/>, label:"Add to Queue",             desc:"Add this post to the selected category queue." },
  ];
  if (!open) return null;
  return (
    <Box sx={{
      position:"absolute", right:0, bottom:"110%",
      background:"#1a1a26", border:`1px solid ${border}`,
      borderRadius:"12px", p:1, zIndex:100, minWidth:320,
      boxShadow:"0 8px 32px rgba(0,0,0,0.4)",
    }}>
      {options.map(o => (
        <Stack key={o.label} direction="row" spacing={1.5} alignItems="flex-start"
          onClick={() => onSelect(o.label)}
          sx={{ px:1.5, py:1.2, borderRadius:"8px", cursor:"pointer", "&:hover":{ background:"#252538" } }}>
          <Box sx={{ color:accent, mt:0.2, flexShrink:0 }}>{o.icon}</Box>
          <Box>
            <Typography fontSize={13} fontWeight={600} color="#e8eaf0">{o.label}</Typography>
            <Typography fontSize={11} color={mutedText}>{o.desc}</Typography>
          </Box>
        </Stack>
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────
// Social Planer View — with working date range picker
// ─────────────────────────────────────────────────────
function SocialPlanerView({ onNewPost }: { onNewPost: () => void }) {
  const today = new Date();

  const [posts, setPosts]               = useState<MarketingPost[]>(MOCK_POSTS);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fromDate, setFromDate]         = useState<Date | null>(new Date(today.getFullYear(), today.getMonth(), 25));
  const [toDate,   setToDate]           = useState<Date | null>(new Date(today.getFullYear(), today.getMonth() + 1, 10));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [connectOpen, setConnectOpen]   = useState(false);
  const [viewGrid, setViewGrid]         = useState(false);
  const datePillRef = useRef<HTMLDivElement>(null);

  const toggleCheck = (id: string) =>
    setPosts(p => p.map(post => post.id === id ? { ...post, checked: !post.checked } : post));

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1}>
        <Box>
          <Typography fontSize={26} fontWeight={700} color="#fff">Marketing</Typography>
          <Typography fontSize={12} color={mutedText}>You can manage your social post here.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => setConnectOpen(true)} startIcon={<AddIcon/>}
            sx={{ textTransform:"none", fontSize:13, fontWeight:600, background:"#1e2030", color:accent, border:`1px solid ${accent}33`, borderRadius:"10px", px:2, py:0.9, whiteSpace:"nowrap", "&:hover":{ background:"#252538" } }}>
            Add Socials
          </Button>
          <Button onClick={onNewPost} startIcon={<AddIcon/>}
            sx={{ textTransform:"none", fontSize:13, fontWeight:700, background:accent, color:"#000", borderRadius:"10px", px:2, py:0.9, whiteSpace:"nowrap", "&:hover":{ background:"#74c0fc" } }}>
            New Post
          </Button>
        </Stack>
      </Stack>

      <Typography fontSize={18} fontWeight={700} color="#fff" mb={2}>Social Planer</Typography>

      {/* Toolbar */}
      <Paper elevation={0} sx={{ p:"14px 18px", borderRadius:"14px", background:cardBg, border:`1px solid ${border}`, mb:1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>

          {/* Post to avatars */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography fontSize={12} color={mutedText} mr={0.5}>Post to</Typography>
            <Stack direction="row">
              {[0,1,2].map(i => (
                <Avatar key={i} src="/images/blogs5.png"
                  sx={{ width:28, height:28, border:`2px solid ${cardBg}`, ml:i>0?-0.8:0 }}/>
              ))}
            </Stack>
            <Select defaultValue="all" size="small" variant="standard" disableUnderline
              sx={{ color:accent, fontSize:11, fontWeight:600, minWidth:20 }}>
              <MenuItem value="all" sx={{ fontSize:11 }}>All</MenuItem>
            </Select>
          </Stack>

          {/* ── CLICKABLE Date range pill ── */}
          <Box sx={{ position:"relative" }} ref={datePillRef}>
            <Stack
              direction="row" spacing={0.5} alignItems="center"
              onClick={() => setDatePickerOpen(prev => !prev)}
              sx={{
                background: datePickerOpen ? "#252538" : "#1e2030",
                border: `1px solid ${datePickerOpen ? accent : border}`,
                borderRadius:"8px", px:1.2, py:0.5,
                cursor:"pointer", userSelect:"none",
                transition:"all 0.15s",
                "&:hover":{ borderColor:accent, background:"#252538" },
              }}
            >
              <CalendarTodayOutlinedIcon sx={{ fontSize:13, color:datePickerOpen?accent:mutedText }}/>
              <Typography fontSize={12} color={datePickerOpen?"#fff":"#c8d0e0"} fontWeight={600}>
                {fromDate ? formatDisplayDate(fromDate) : "Start"} → {toDate ? formatDisplayDate(toDate) : "End"}
              </Typography>
              <CalendarTodayOutlinedIcon sx={{ fontSize:13, color:datePickerOpen?accent:mutedText }}/>
            </Stack>

            <DateRangePicker
              open={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              fromDate={fromDate}
              toDate={toDate}
              onApply={(from, to) => { setFromDate(from); setToDate(to); }}
              anchorRef={datePillRef as React.RefObject<HTMLElement>}
            />
          </Box>

          {/* Search */}
          <Box sx={{ display:"flex", alignItems:"center", background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", px:1.2, py:0.5, flex:1, minWidth:120 }}>
            <SearchIcon sx={{ fontSize:14, color:mutedText, mr:0.5 }}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              style={{ background:"transparent", border:"none", outline:"none", color:"#c8d0e0", fontSize:12, width:"100%" }}
            />
          </Box>

          {/* Status filter */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {["All","Published","Draft","Need Approval","Scheduled"].map(s => (
              <Button key={s} size="small" onClick={() => setStatusFilter(s)}
                sx={{
                  textTransform:"none", fontSize:11, fontWeight:600, borderRadius:"6px", px:1, py:0.4,
                  color: statusFilter===s ? "#fff" : mutedText,
                  background: statusFilter===s ? "#252538" : "transparent",
                  border: statusFilter===s ? `1px solid ${border}` : "1px solid transparent",
                  "&:hover":{ background:"#252538" },
                }}>
                {s}
              </Button>
            ))}
          </Stack>

          {/* View toggle */}
          <Stack direction="row" sx={{ background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", p:"2px" }}>
            <IconButton size="small" onClick={() => setViewGrid(false)}
              sx={{ color:!viewGrid?"#fff":mutedText, background:!viewGrid?"#0f1016":"transparent", borderRadius:"5px", p:0.5 }}>
              <ListIcon sx={{ fontSize:16 }}/>
            </IconButton>
            <IconButton size="small" onClick={() => setViewGrid(true)}
              sx={{ color:viewGrid?"#fff":mutedText, background:viewGrid?"#0f1016":"transparent", borderRadius:"5px", p:0.5 }}>
              <GridViewIcon sx={{ fontSize:16 }}/>
            </IconButton>
          </Stack>

          {/* Filters btn */}
          <Button startIcon={<FilterListIcon sx={{ fontSize:14 }}/>} size="small"
            sx={{ textTransform:"none", fontSize:12, color:mutedText, background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", px:1.5, py:0.5, "&:hover":{ color:"#fff" } }}>
            Filters
          </Button>
        </Stack>
      </Paper>

      {/* Posts table */}
      <Paper elevation={0} sx={{ borderRadius:"14px", background:cardBg, border:`1px solid ${border}`, overflow:"hidden" }}>
        <Stack direction="row" alignItems="center" sx={{ px:"18px", py:"10px", borderBottom:`1px solid ${border}`, background:"#1a1a26" }}>
          <Box sx={{ width:32 }}><CheckBoxOutlineBlankIcon sx={{ fontSize:16, color:mutedText }}/></Box>
          <Typography fontSize={11} color={mutedText} fontWeight={700} sx={{ flex:1 }}>CONTENT</Typography>
          <Typography fontSize={11} color={mutedText} fontWeight={700} sx={{ width:130, textAlign:"center" }}>STATUS</Typography>
          <Typography fontSize={11} color={mutedText} fontWeight={700} sx={{ width:160, textAlign:"center" }}>DATE</Typography>
          <Typography fontSize={11} color={mutedText} fontWeight={700} sx={{ width:200, textAlign:"center" }}>SOCIAL</Typography>
          <Box sx={{ width:40 }}/>
        </Stack>

        {posts
          .filter(p => statusFilter==="All" || p.status===statusFilter)
          .filter(p => p.content.toLowerCase().includes(search.toLowerCase()))
          .map(post => (
            <Stack key={post.id} direction="row" alignItems="center"
              sx={{ px:"18px", py:"14px", borderBottom:`1px solid ${border}`, "&:last-child":{ borderBottom:"none" }, "&:hover":{ background:"#1a1a24" }, transition:"background 0.15s" }}>
              <Box sx={{ width:32, cursor:"pointer" }} onClick={() => toggleCheck(post.id)}>
                {post.checked
                  ? <CheckCircleIcon sx={{ fontSize:16, color:accent }}/>
                  : <CheckBoxOutlineBlankIcon sx={{ fontSize:16, color:mutedText }}/>}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex:1, minWidth:0 }}>
                <SmallTypeIcon type={post.postType}/>
                <Typography fontSize={13} color="#e8eaf0" noWrap sx={{ maxWidth:320 }}>{post.content}</Typography>
              </Stack>
              <Box sx={{ width:130, textAlign:"center" }}><StatusBadge status={post.status}/></Box>
              <Typography fontSize={12} color={mutedText} sx={{ width:160, textAlign:"center" }}>{post.date}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ width:200 }} justifyContent="center" flexWrap="wrap" gap={0.3}>
                {post.tags.map(t => <TagChip key={t} label={t}/>)}
              </Stack>
              <Box sx={{ width:40, textAlign:"right" }}>
                <IconButton size="small" sx={{ color:mutedText, p:0.3 }}>
                  <MoreHorizIcon sx={{ fontSize:18 }}/>
                </IconButton>
              </Box>
            </Stack>
          ))}
      </Paper>

      <ConnectSocialModal open={connectOpen} onClose={() => setConnectOpen(false)}/>
    </Box>
  );
}

// ─────────────────────────────────────────────────────
// New Social Post View
// ─────────────────────────────────────────────────────
function NewSocialPostView({ onBack }: { onBack: () => void }) {
  const [content, setContent] = useState(
    `LinkedIn Thought Leadership Post - Chapter 1: Artificial Intelligence\n\nMost people talk about AI as a tool.\nI see it as the operating system of the next civilization.\n\nIn The Convergence of AI and the Top 10 Emerging Technologies, I start with AI for one simple reason:\nAI is not "one technology among many."\nIt is the force connecting, accelerating, and amplifying every other emerging technology.`
  );
  const [activeChannel,   setActiveChannel]   = useState("Original");
  const [scheduleOpen,    setScheduleOpen]    = useState(false);
  const [scheduleTitle,   setScheduleTitle]   = useState("Schedule Post");
  const [postOptionsOpen, setPostOptionsOpen] = useState(false);
  const [connectOpen,     setConnectOpen]     = useState(false);
  const [selectedSocials, setSelectedSocials] = useState([0,1,2]);

  const channels = ["Original","Facebook","Instagram","LinkedIn","Twitter"];
  const channelIcons: Record<string, React.ReactNode> = {
    Facebook:  <FacebookIcon  sx={{ fontSize:16, color:"#1877f2" }}/>,
    Instagram: <InstagramIcon sx={{ fontSize:16, color:"#e1306c" }}/>,
    LinkedIn:  <LinkedInIcon  sx={{ fontSize:16, color:"#0a66c2" }}/>,
    Twitter:   <TwitterIcon   sx={{ fontSize:16, color:"#1da1f2" }}/>,
  };

  const handlePostOption = (val: string) => {
    setPostOptionsOpen(false);
    if (["Schedule Post","Send Post for Approval","Schedule Recurring Posts"].includes(val)) {
      setScheduleTitle(val);
      setScheduleOpen(true);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={onBack}
            sx={{ color:mutedText, border:`1px solid ${border}`, borderRadius:"8px", p:0.6, "&:hover":{ color:"#fff" } }}>
            <ArrowBackIcon sx={{ fontSize:18 }}/>
          </IconButton>
          <Typography fontSize={20} fontWeight={700} color="#fff">New Social Post</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<BookmarkBorderIcon sx={{ fontSize:15 }}/>}
            sx={{ textTransform:"none", fontSize:12, fontWeight:600, color:mutedText, background:"#1e2030", border:`1px solid ${border}`, borderRadius:"10px", px:2, py:0.9, "&:hover":{ color:"#fff" } }}>
            Save for later
          </Button>
          <Button sx={{ textTransform:"none", fontSize:13, fontWeight:700, background:accent, color:"#000", borderRadius:"10px", px:2.5, py:0.9, "&:hover":{ background:"#74c0fc" } }}>
            New Post
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p:"20px 22px", borderRadius:"14px", background:cardBg, border:`1px solid ${border}` }}>
            <Typography fontSize={13} fontWeight={700} color="#fff" mb={1}>Select Socials</Typography>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Stack direction="row">
                {[0,1,2,3,4,5,6,7].map(i => (
                  <Avatar key={i} src="/images/blogs5.png"
                    onClick={() => setSelectedSocials(prev => prev.includes(i) ? prev.filter(x=>x!==i) : [...prev,i])}
                    sx={{ width:32, height:32, ml:i>0?-0.6:0, cursor:"pointer",
                      border: selectedSocials.includes(i) ? `2px solid ${accent}` : `2px solid ${cardBg}`,
                      transition:"all 0.15s" }}/>
                ))}
              </Stack>
              <Button onClick={() => setConnectOpen(true)} startIcon={<AddIcon sx={{ fontSize:14 }}/>} size="small"
                sx={{ textTransform:"none", fontSize:12, fontWeight:600, color:accent, background:"rgba(77,171,247,0.1)", border:`1px solid ${accent}33`, borderRadius:"8px", px:1.5, py:0.5, ml:1, "&:hover":{ background:"rgba(77,171,247,0.18)" } }}>
                Socials
              </Button>
            </Stack>

            <Divider sx={{ borderColor:border, mb:2 }}/>

            <Typography fontSize={13} fontWeight={700} color="#fff" mb={1}>Customize for each channel</Typography>
            <Stack direction="row" spacing={0.5} mb={2}>
              {channels.map(ch => (
                <Button key={ch} size="small" onClick={() => setActiveChannel(ch)}
                  startIcon={ch!=="Original" ? channelIcons[ch] : undefined}
                  sx={{ textTransform:"none", fontSize:12, fontWeight:600,
                    color: activeChannel===ch ? "#fff" : mutedText,
                    borderBottom: activeChannel===ch ? `2px solid ${accent}` : "2px solid transparent",
                    borderRadius:0, pb:0.5, px:1, "&:hover":{ color:"#fff" } }}>
                  {ch}
                </Button>
              ))}
            </Stack>

            <Typography fontSize={13} fontWeight={700} color="#fff" mb={1}>Type content</Typography>
            <Box sx={{ background:"#0f1016", border:`1px solid ${border}`, borderRadius:"10px", p:"14px 16px", mb:2 }}>
              <TextareaAutosize value={content} onChange={e=>setContent(e.target.value)} minRows={7}
                style={{ width:"100%", background:"transparent", border:"none", outline:"none", color:"#e8eaf0", fontSize:13, lineHeight:1.6, resize:"none", fontFamily:"inherit" }}/>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" startIcon={<BoltIcon sx={{ fontSize:13 }}/>}
                    sx={{ textTransform:"none", fontSize:11, fontWeight:700, color:"#000", background:accent, borderRadius:"6px", px:1, py:0.3, "&:hover":{ background:"#74c0fc" } }}>
                    AI
                  </Button>
                  {[<FormatBoldIcon/>,<FormatItalicIcon/>,<EmojiEmotionsOutlinedIcon/>,<ImageOutlinedIcon/>,<TagOutlinedIcon/>,<LinkOutlinedIcon/>].map((ic,i) => (
                    <IconButton key={i} size="small" sx={{ color:mutedText, p:0.3, "&:hover":{ color:"#fff" } }}>
                      {React.cloneElement(ic as React.ReactElement, { sx:{ fontSize:15 } })}
                    </IconButton>
                  ))}
                </Stack>
                <Typography fontSize={11} color={mutedText}>{content.length}/1091</Typography>
              </Stack>
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ background:"#1a1a26", border:`1px solid ${border}`, borderRadius:"10px", px:2, py:1.2, mb:1.5, cursor:"pointer", "&:hover":{ borderColor:`${accent}44` } }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography fontSize={13} fontWeight={600} color="#c8d0e0">Advanced Options</Typography>
                <InfoOutlinedIcon sx={{ fontSize:14, color:mutedText }}/>
              </Stack>
              <NavigateNextIcon sx={{ fontSize:18, color:mutedText }}/>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ background:"#1a1a26", border:`1px solid ${border}`, borderRadius:"10px", px:2, py:1.2, mb:1.5 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography fontSize={13} fontWeight={600} color="#c8d0e0">Add Follow-up comment</Typography>
                <InfoOutlinedIcon sx={{ fontSize:14, color:mutedText }}/>
              </Stack>
              <Button size="small"
                sx={{ textTransform:"none", fontSize:12, fontWeight:600, color:accent, background:"rgba(77,171,247,0.1)", border:`1px solid ${accent}33`, borderRadius:"7px", px:1.5, py:0.4 }}>
                Add Comment
              </Button>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ background:"#1a1a26", border:`1px solid ${border}`, borderRadius:"10px", px:2, py:1.2, mb:2 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography fontSize={13} fontWeight={600} color="#c8d0e0">Post Options</Typography>
                <InfoOutlinedIcon sx={{ fontSize:14, color:mutedText }}/>
              </Stack>
              <Select defaultValue="On Feed" size="small" variant="outlined"
                sx={{ color:"#c8d0e0", fontSize:12, fontWeight:600, background:"#0f1016", borderRadius:"7px",
                  "& .MuiOutlinedInput-notchedOutline":{ borderColor:border },
                  "& .MuiSelect-icon":{ color:mutedText }, minWidth:100 }}>
                <MenuItem value="On Feed" sx={{ fontSize:12 }}>On Feed</MenuItem>
                <MenuItem value="Story"   sx={{ fontSize:12 }}>Story</MenuItem>
                <MenuItem value="Reel"    sx={{ fontSize:12 }}>Reel</MenuItem>
              </Select>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ background:"#1a1a26", border:`1px solid ${border}`, borderRadius:"10px", px:2, py:1.2, mb:3 }}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography fontSize={13} fontWeight={600} color="#c8d0e0">Facebook Options</Typography>
                <InfoOutlinedIcon sx={{ fontSize:14, color:mutedText }}/>
              </Stack>
              <NavigateNextIcon sx={{ fontSize:18, color:mutedText }}/>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              <Button startIcon={<BookmarkBorderIcon sx={{ fontSize:14 }}/>}
                sx={{ textTransform:"none", fontSize:12, fontWeight:600, color:mutedText, background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", px:2, py:0.8 }}>
                Save for later
              </Button>
              <Box sx={{ position:"relative" }}>
                <Button onClick={() => setPostOptionsOpen(prev=>!prev)}
                  endIcon={<NavigateNextIcon sx={{ fontSize:14, transform:"rotate(90deg)" }}/>}
                  sx={{ textTransform:"none", fontSize:13, fontWeight:700, background:"#1e2030", color:"#fff", border:`1px solid ${border}`, borderRadius:"8px", px:2, py:0.8, "&:hover":{ background:"#252538" } }}>
                  Manage Post
                </Button>
                <PostOptionsDropdown open={postOptionsOpen} onClose={()=>setPostOptionsOpen(false)} onSelect={handlePostOption}/>
              </Box>
              <Button startIcon={<SendIcon sx={{ fontSize:14 }}/>}
                sx={{ textTransform:"none", fontSize:13, fontWeight:700, background:accent, color:"#000", borderRadius:"8px", px:2, py:0.8, "&:hover":{ background:"#74c0fc" } }}>
                Post Now
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p:"18px 20px", borderRadius:"14px", background:cardBg, border:`1px solid ${border}` }}>
            <Typography fontSize={14} fontWeight={700} color="#fff" mb={1.5}>Post Previews</Typography>
            <Stack direction="row" spacing={0.5} mb={2} alignItems="center">
              <Typography fontSize={12} color={mutedText} mr={0.5}>All</Typography>
              {[
                { icon:<FacebookIcon  sx={{ fontSize:18, color:"#1877f2" }}/> },
                { icon:<LinkedInIcon  sx={{ fontSize:18, color:"#0a66c2" }}/> },
                { icon:<InstagramIcon sx={{ fontSize:18, color:"#e1306c" }}/> },
                { icon:<TwitterIcon   sx={{ fontSize:18, color:"#1da1f2" }}/> },
              ].map((ch,i) => (
                <IconButton key={i} size="small"
                  sx={{ background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", p:0.6, "&:hover":{ borderColor:accent } }}>
                  {ch.icon}
                </IconButton>
              ))}
            </Stack>

            <Box sx={{ background:"#0f1016", border:`1px solid ${border}`, borderRadius:"12px", p:"16px", mb:2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar src="/images/blogs5.png" sx={{ width:36, height:36 }}/>
                  <Box>
                    <Typography fontSize={13} fontWeight={700} color="#fff">Ocesha Ai</Typography>
                    <Typography fontSize={11} color={mutedText}>JUST NOW</Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color:mutedText, p:0.3 }}>
                  <MoreHorizIcon sx={{ fontSize:16 }}/>
                </IconButton>
              </Stack>
              <Typography fontSize={12} color="#c8d0e0" lineHeight={1.6}
                sx={{ display:"-webkit-box", WebkitLineClamp:12, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                {content}
              </Typography>
              <Divider sx={{ borderColor:border, mt:2, mb:1.5 }}/>
              <Stack direction="row" spacing={2}>
                {["Like","Comment","Share"].map(a => (
                  <Button key={a} size="small" sx={{ textTransform:"none", fontSize:11, color:mutedText, "&:hover":{ color:"#fff" } }}>{a}</Button>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button startIcon={<BookmarkBorderIcon sx={{ fontSize:13 }}/>} size="small"
                sx={{ textTransform:"none", fontSize:12, color:mutedText, background:"#1e2030", border:`1px solid ${border}`, borderRadius:"8px", px:1.5, py:0.6 }}>
                + Save for later
              </Button>
              <Button startIcon={<SendIcon sx={{ fontSize:13 }}/>} size="small"
                sx={{ textTransform:"none", fontSize:12, fontWeight:700, background:accent, color:"#000", borderRadius:"8px", px:1.5, py:0.6, "&:hover":{ background:"#74c0fc" } }}>
                Post
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <ConnectSocialModal open={connectOpen} onClose={() => setConnectOpen(false)}/>
      <ScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} title={scheduleTitle}/>
    </Box>
  );
}

// ═════════════════════════════════════════════════════
// Main Component: Marketing
// ═════════════════════════════════════════════════════

const Marketing = () => {
  const navigate   = useNavigate();
  const dispatch   = useAppDispatch();
  const [activeItem,  setActiveItem]  = useState(localStorage.getItem("activeItem") || "marketing");
  const [isMenuOpen,  setIsMenuOpen]  = useState(false);
  const [planDetails, setPlanDetails] = useState<any>();
  const [helperPopup, setHelperPopup] = useState(false);
  const [helpervideosrc, setHelperVideoSrc] = useState<any>();
  const [view, setView]               = useState<ViewMode>("list");
  const [getuserPlanDetails]          = usePlanDetailsMutation();

  const user_email = localStorage.getItem("user_email") || getCookie("user_email") || "";

  useEffect(() => {
    (async () => {
      try {
        const r = await getuserPlanDetails({ email: user_email, campaignid: "" });
        if ("data" in r) setPlanDetails(r.data.data);
      } catch (e) {} 
    })();
  }, []);

  const clearCookies = () =>
    document.cookie.split("; ").forEach(c => {
      const [n] = c.split("=");
      document.cookie = `${n}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
    });

  const handleItemClick = (itemName: any) => {
    localStorage.setItem("activeItem", itemName);
    setActiveItem(itemName);
    ["campaignid","returningtocampaign","sourceurl","campaignName","paymentOption","autoPublish","limitPublish",
     "smUrlpublish","addTags","captureLeads","newsLetter","twitterBtn","fbBtn","pintrestBtn","linkedinBtn",
     "video","twitterurl","fburl","pintresturl","linkedinurl","owndomain","ChangepayMode","IdChangingMode",
     "PaymentSubscription","Authorurl","Authorname","authorDefaultCheck","videoTag","podcastUrl","sourcetext",
    ].forEach(k => localStorage.removeItem(k));
    if      (itemName==="createcontent")   navigate("/codegenie/new-podcast");
    else if (itemName==="createcourse")    navigate("/codegenie/new-course");
    else if (itemName==="course")          navigate("/codegenie/generated-courses");
    else if (itemName==="authorityEngine") navigate("/codegenie/authorityEngine");
    else if (itemName==="socialPost")      navigate("/codegenie/socialPost");
    else if (itemName==="marketing")       navigate("/codegenie/marketing");
    else if (itemName==="upgrade") {
      localStorage.setItem("fromUpgrade","true");
      navigate("/pricing",{ state:{ campIdFromUpgrade:localStorage.getItem("campId")||"", from_page:"blog_listing" } });
    } else if (itemName==="logout") {
      clearCookies(); localStorage.setItem("logged_in","false"); dispatch(logout());
      toast.success("Logout success"); localStorage.clear(); removeCookie("user_email"); window.location.href = process.env.REACT_APP_LOGOUT_DOMAIN||''; //"https://ocesha.ai";
    } else {
      navigate("/codegenie/generated-blogs");
    }
  };


  return (
    <>
           <div style={{ width: '100%', overflowX: 'hidden' }}>
        <div className="col-sm-12 col-md-12 col-lg-12">
            <div className="overlay-container">
              <div style={{ width:"98%", marginBottom:"10%", padding:"0 1%" }}>
                {view==="list"
                  ? <SocialPlanerView onNewPost={()=>setView("new_post")}/>
                  : <NewSocialPostView onBack={()=>setView("list")}/>
                }
              </div>
            </div>
          </div>

      {helperPopup && (
        <div style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.75)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#282C39", borderRadius:"15px", padding:"3%", width:"70%", maxWidth:"900px" }}>
            <iframe width="100%" height="500" src={helpervideosrc} title="Help Video" frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen/>
            <div style={{ display:"flex", justifyContent:"center", marginTop:"15px" }}>
              <Button onClick={()=>setHelperPopup(false)} variant="contained">Close</Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Marketing;